/**
 * Premium Button Component
 * Gel-style buttons with glassmorphism and micro-interactions
 */

import { soundFx } from '../../lib/soundEffects'

export const Button = ({
  children,
  variant = 'primary',
  size = 'md',
  className = '',
  icon,
  onClick,
  sound = 'click', // Default sound
  ...props
}) => {
  // Base styles - Premium gel button foundation
  const baseStyles = `
    relative inline-flex items-center justify-center gap-2
    font-semibold transition-all duration-200
    disabled:opacity-40 disabled:cursor-not-allowed
    focus:outline-none focus:ring-2 focus:ring-offset-2
    transform active:scale-[0.98]
  `.trim().replace(/\s+/g, ' ')

  // Standard variants
  const variants = {
    primary: `
      bg-indigo-600 text-white border border-transparent
      hover:bg-indigo-700 hover:shadow-lg
      focus:ring-indigo-500
      shadow-md
    `.trim().replace(/\s+/g, ' '),

    secondary: `
      bg-white text-gray-700 border border-gray-200
      hover:bg-gray-50 hover:border-gray-300 hover:shadow-sm
      focus:ring-indigo-500
      shadow-sm
    `.trim().replace(/\s+/g, ' '),

    outline: `
      bg-transparent text-gray-600 border border-gray-300
      hover:bg-gray-50 hover:text-gray-800
      focus:ring-indigo-500
    `.trim().replace(/\s+/g, ' '),

    ghost: `
      bg-transparent text-gray-600 cursor-pointer
      hover:bg-gray-100 hover:text-gray-900
      focus:ring-gray-400
    `.trim().replace(/\s+/g, ' '),

    danger: `
      bg-rose-50 text-rose-700 border border-rose-200
      hover:bg-rose-100 hover:border-rose-300 hover:text-rose-800
      focus:ring-rose-400
      shadow-sm
    `.trim().replace(/\s+/g, ' '),

    'danger-solid': `
      bg-red-600 text-white border border-transparent
      hover:bg-red-700
      focus:ring-red-500
      shadow-sm opacity-90 hover:opacity-100
    `.trim().replace(/\s+/g, ' '),

    warning: `
      bg-amber-500 text-white border border-transparent
      hover:bg-amber-600
      focus:ring-amber-400
      shadow-sm
    `.trim().replace(/\s+/g, ' '),

    success: `
      bg-emerald-600 text-white border border-transparent
      hover:bg-emerald-700 hover:shadow-md
      focus:ring-emerald-500
      shadow-sm
    `.trim().replace(/\s+/g, ' ')
  }

  // Size variants with premium spacing
  const sizes = {
    xs: 'px-3 py-1.5 text-xs rounded-lg',
    sm: 'px-4 py-2 text-sm rounded-xl',
    md: 'px-6 py-2.5 text-base rounded-xl',
    lg: 'px-8 py-3 text-lg rounded-2xl',
    xl: 'px-10 py-4 text-xl rounded-2xl'
  }

  const handleClick = (e) => {
    // Play sound if not disabled
    if (!props.disabled && sound) {
      soundFx.play(sound)
      e.audioplayed = true
    }
    // Call original onClick
    if (onClick) onClick(e)
  }

  return (
    <button
      className={`${baseStyles} ${variants[variant]} ${sizes[size]} ${className}`}
      onClick={handleClick}
      {...props}
    >
      {icon && <span className="inline-flex">{icon}</span>}
      {children}
    </button>
  )
}
